import { useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import { useEffect, useState } from 'react';

import { useHeroToast } from '@/hooks';

import { fetchPaginatedCollectionByOrganizationRealtime } from '../../../queries';
import { DeviceDataModel, DeviceFirebaseResponse } from './types';

export const useGetDevicesFromFirebase = (
  id: string,
  page?: number,
  elements?: number,
  options?: UseQueryOptions<DeviceDataModel, FirebaseError, DeviceDataModel>,
): UseQueryResult<DeviceDataModel, FirebaseError> => {
  const queryClient = useQueryClient();
  const { showError } = useHeroToast();
  const subscriptionKey = ['pods', page ?? 1, elements ?? 9, id];

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<FirebaseError | null>(null);

  useEffect(() => {
    if (!id) {
      showError('useGetDevicesFromFirebase: Invalid id. Skipping subscription.');
      return;
    }

    const unsubscribePromise = fetchPaginatedCollectionByOrganizationRealtime<DeviceFirebaseResponse>(
      'pods',
      id,
      page ?? 1,
      elements ?? 9,
      'createdAt',
      (data: DeviceFirebaseResponse[], totalPages : number) => {
        const mappedData = data.map((item: DeviceFirebaseResponse) => ({
          id: item.id,
          name: item.name,
          model: item.model,
          homeScreenWelcomeText: item.homeScreenWelcomeText,
          createdAt: item.createdAt,
        }));
        setIsLoading(false);
        queryClient.setQueryData<DeviceDataModel>(subscriptionKey, {
          data: mappedData,
          totalPages,
          isLoading: false,
          error: null,
        });
      },
      (e : FirebaseError) => {
        setError(e as FirebaseError);
        setIsLoading(false);
      },
    );

    // eslint-disable-next-line consistent-return
    return () :void => {
      unsubscribePromise
        .then((unsubscribe : () => void) => {
          if (typeof unsubscribe === 'function') {
            unsubscribe();
          }
        })
        .catch((e : FirebaseError) => {
          showError(`Failed to unsubscribe:', ${e}`);
        });
    };
  }, [id, page, elements, queryClient]);

  return useQuery<DeviceDataModel, FirebaseError, DeviceDataModel>({
    queryKey: subscriptionKey,
    queryFn: async () => {
      const cachedData = queryClient.getQueryData<DeviceDataModel>(subscriptionKey);
      if (cachedData) {
        return cachedData;
      }
      return { data: [], totalPages: 0, isLoading, error };
    },
    enabled: !!id,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
};
